import { Component, computed, ElementRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { filter, map } from 'rxjs/operators';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DateRangeFilter, DateRangeTypeEnum, EnumFilterType, FilterItemList, PageTypeEnum, SelectedFilter } from '../../models/filter.model';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { toSignal } from '@angular/core/rxjs-interop';
import { GlobalStore } from 'src/app/store/global-store';
import { AutoCompleteFilterComponent } from '../autocomplete-filter/autocomplete-filter.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { DualCalendarRangePickerComponent, ICalendarResults } from '../dual-calendar-range-picker/dual-calendar-range-picker.component';
import { ManipulationsToolbarStore } from 'src/app/store/manipulations-toolbar-store';
import { CategoryFilterComponent } from "../category-filter/category-filter.component";
import { StatusFilterComponent } from "../status-filter/status-filter.component";
import { environment } from 'src/environment';
import { StatusToUserFilterComponent } from '../status-to-user-filter/status-to-user-filter.component';
import { StatusApprovalFilterComponent } from '../status-approval-filter/status-approval-filter.component';
import { NumberFilterComponent } from '../number-filter/number-filter.component';
import { MlmExportStatusFilterComponent } from '../mlm-export-status-filter/mlm-export-status-filter.component';
import { AmountRangeFilterComponent } from '../amount-range-filter/amount-range-filter.component';
import { ClientChargeFilterComponent } from '../client-charge-filter/client-charge-filter.component';
import { FormStatusFilterComponent } from '../form-status-filter/form-status-filter.component';
import { PayrollMonthFilterComponent } from '../payroll-month-filter/payroll-month-filter.component';

@Component({
    selector: 'que-filter-type',
    templateUrl: './filter-type.component.html',
    styleUrls: ['./filter-type.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        AutoCompleteFilterComponent,
        CommonModule,
        OverlayModule,
        TranslateDirective, TranslatePipe,
        DualCalendarRangePickerComponent,
        CategoryFilterComponent,
        StatusFilterComponent,
        StatusToUserFilterComponent,
        StatusApprovalFilterComponent, 
        NumberFilterComponent,
    MlmExportStatusFilterComponent,
    AmountRangeFilterComponent,
    ClientChargeFilterComponent,
    FormStatusFilterComponent,
    PayrollMonthFilterComponent
    ]
})

export class FilterTypeComponent implements OnInit {
  @Input() filter = <FilterItemList>{};
  @ViewChild('filterType') filterType!: ElementRef;
  @ViewChild('dateRangeToSelect') dateRangeToSelect!: ElementRef;
  #globalStore = inject(GlobalStore);
  #manipulationsToolbarStore = inject(ManipulationsToolbarStore);
  selectedFilterSig$: any = toSignal(this.#manipulationsToolbarStore.selectedFilter$)
  selectedFilter$ = this.#manipulationsToolbarStore.selectedFilter$
  categories$ = toSignal(this.#globalStore.categories$)
  names$ = toSignal(this.#globalStore.names$);
  senderNames$ = toSignal(this.#manipulationsToolbarStore.senderNames$);
  reportApproverNames$ = toSignal(this.#manipulationsToolbarStore.reportApproverNames$);
  isAdmin$ = toSignal(this.#globalStore.isRoleAdmin$);
  isDisplayUser$ = toSignal(this.#manipulationsToolbarStore.isDisplayUser$);
  // The "creator" filter is shared: on the Questionnaires grid it lists questionnaire creators
  // (admins), but on the Approvals page (including its Reports tab) it must list the senders of the
  // requests actually shown there (see ManipulationsToolbarStore.senderNames$) - not the same
  // global list.
  creatorFilterOptions$ = computed(() =>
    this.#manipulationsToolbarStore.pageTypeSig$() === PageTypeEnum.Approvals
      ? this.senderNames$()
      : this.names$()
  );
  DateRangeTypeEnum = DateRangeTypeEnum
  customDateRangeType = DateRangeTypeEnum.Custom;
  isFilterTypeOpen = false;
  selectedRangeIsOpened = false;
  readonly positonArea = [{
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
  }] as ConnectedPosition[];

  readonly positonAreaForSelectedRange = [{
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
  }] as ConnectedPosition[];
  readonly dateRangeFilter$ = this.#manipulationsToolbarStore.dateRangeFilter$;
  readonly valueControl = new FormControl<any>(null);
  selectedFilter1: SelectedFilter | null | undefined;
  public get enumFilterType(): typeof EnumFilterType {
    return EnumFilterType;
  }

  constructor() {
    this.#manipulationsToolbarStore.selectedFilter$.subscribe(filter => {
      this.selectedFilter1 = filter;
    });
  }

  ngOnInit(): void {
    this.valueControl.valueChanges
      .pipe(
        map((valueControl) => {
          let changes: { [x: string]: any } = {};
          changes[this.filter.fieldName] = valueControl;
          return changes;
        }),
        filter((changes) => !this.valueControl.invalid)
      )
      .subscribe((value) => {
        this.#manipulationsToolbarStore.updateFilterDataSelected(value);
        // Multi-select fields (checkbox lists / multi-autocomplete) and the two-input amount range
        // stay open across repeated commits; single-value fields close the panel as soon as a value
        // is committed.
        if (this.filter.fieldName != "creator" && this.filter.fieldName != "status"
          && this.filter.fieldName != "approverIds" && this.filter.fieldName != "mlmExportStatus"
          && this.filter.fieldName != "formStatus" && this.filter.fieldName != "amount"
          && this.filter.fieldName != "payrollMonths")
          this.isFilterTypeOpen = false
      });
  }

  dateRangePicked(dateRange: ICalendarResults, fieldName: string) {
    if (dateRange.endDate && dateRange.startDate) {
      this.#manipulationsToolbarStore.updateFilterDataSelected({
        [fieldName as keyof SelectedFilter]: {
          dateRangeType: DateRangeTypeEnum.Custom,
          customDateToNTZ: dateRange.endDate,
          customDateFromNTZ: dateRange.startDate,
        }
      });
      this.selectedRangeIsOpened = false;
      // this.isFilterTypeOpen = false;
    }
  }

  resetFilter(key: string, event: Event) {
    event.stopPropagation();
    this.#manipulationsToolbarStore.removeFilter([key]);
  }

  // `in` only checks key presence, not value - clearing a filter back to null (e.g. clicking an
  // already-selected yes/no option, or clearing a number/range field) leaves the key in
  // selectedFilter with a null value, which `in` still counts as "exists". Filters whose "empty"
  // state is an empty array (creator/status) go through a different removeFilter([...]) path in
  // updateFilterDataSelected and were never affected by this - only the null-valued ones were.
  filterExists(filterName: string): boolean {
    if (!this.selectedFilterSig$()) return false;
    const value = (this.selectedFilterSig$() as any)[filterName];
    return value !== undefined && value !== null;
  }
  filterNumber(filterName: string) {
    if (!this.selectedFilterSig$())
      return 0;
    else {
      const filterValue = this.selectedFilterSig$()[filterName];
      if (Array.isArray(filterValue)) {
        return filterValue.length; // Return the length of the array
      } else {
        return filterValue !== undefined && filterValue !== null ? 1 : 0; // Check if the field exists and is not null
      }
    }
  }
  setDisplayDetailsFilter(filter: FilterItemList) {
    const triggerRect = this.filterType?.nativeElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    const isRTL = environment.lang === 'he';
    // Check if the menu would open out of the viewport
    if (isRTL) {
      if (triggerRect.left - 200 < 0) {
        this.positonArea[0].overlayX = 'end';
        this.positonArea[0].originX = 'end';
      } else {
        this.positonArea[0].overlayX = 'start';
        this.positonArea[0].originX = 'start';
      }
    } else {

      if ((triggerRect.right + 200) > viewportWidth) {

        this.positonArea[0].overlayX = 'end';
        this.positonArea[0].originX = 'end';
      } else {
        this.positonArea[0].overlayX = 'start';
        this.positonArea[0].originX = 'start';
      }
    }

    this.isFilterTypeOpen = true
  }

  changeDateRange(dateRange: DateRangeFilter, i: number, fieldName: string) {
    if (dateRange.key === DateRangeTypeEnum.Custom) {
      const triggerRect = this.filterType?.nativeElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

      const isRTL = environment.lang === 'he';
      // Check if the menu would open out of the viewport
      if (isRTL) {
        if (triggerRect.left - 170 < 0) {
          this.positonAreaForSelectedRange[0].overlayX = 'end';
          this.positonAreaForSelectedRange[0].originX = 'end';
        } else {
          if (triggerRect.left - 400 < 0) {
            this.positonAreaForSelectedRange[0].overlayX = 'center';
            this.positonAreaForSelectedRange[0].originX = 'center';
          } else {
            this.positonAreaForSelectedRange[0].overlayX = 'start';
            this.positonAreaForSelectedRange[0].originX = 'end';
          }
        }
      } else {

        if ((triggerRect.right + 170) > viewportWidth) {
          this.positonAreaForSelectedRange[0].overlayX = 'end';
          this.positonAreaForSelectedRange[0].originX = 'end';
        } else {
          if (triggerRect.right + 400 >viewportWidth) {
            this.positonAreaForSelectedRange[0].overlayX = 'center';
            this.positonAreaForSelectedRange[0].originX = 'center';
          } else {
            this.positonAreaForSelectedRange[0].overlayX = 'start';
            this.positonAreaForSelectedRange[0].originX = 'end';
          }
        }
      }
      this.selectedRangeIsOpened = true;
    }
    else {
      this.selectedRangeIsOpened = false;
      this.isFilterTypeOpen = false;
      this.#manipulationsToolbarStore.updateFilterDataSelected({
        [fieldName as keyof SelectedFilter]: { dateRangeType: dateRange.key as DateRangeTypeEnum }
      });
    }

  }
}